import { notify,
	type SDK_UpdateStackMutationVariables,
	clientConfig,
	createKey,
	detectBrowserLanguage,
	defaultNavigationIndex,
	getSdk,
	moveByOptions,
	reservedWordsRegEx,
	StyledPillButton,
	text,
	trailingSlashRegEX,
	urlSafeRegEx,
} from "@quickpoint.me/shared";
import { getAuth, getIdToken } from "firebase/auth";
import { GraphQLClient } from "graphql-request";
import { nanoid } from "nanoid";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import {
	useRecoilRefresher_UNSTABLE,
	useRecoilState,
	useRecoilValue,
	useSetRecoilState,
} from "recoil";
import { sendCodeToViewer } from "../../lib/messageBus";
import { isUpgradeCode, returnCodeMessage } from "../../lib/returnCodeMessage";
import { HostId, IsUploading, RenameFamily } from "../../state/atoms";
import { Authorization } from "../../state/authAtoms";
import { OwnerId, OwnerName } from "../../state/authSelectors";
import { CurrentlySelectedFilePath } from "../../state/files";
import { LocaleAtom } from "../../state/localeAtom";
import {
	CreateNewStack,
	ShowStacks,
	StackCfKeyAtom,
} from "../../state/stackAtoms";
import { StackCfKeysSelector, emptyStack } from "../../state/stackSelectors";
import { ShowTknsPanelAtom } from "../../state/tknsAtoms";
import { OpenedInTabStories, Orientation } from "../../state/uiAtoms";
import { StyledError } from "../auth/styled/Components";
import { StyledFile } from "./File";


interface FormValue {
	userDefinedStackName: string;
}

export function EditableFile({
	stackCfKey,
	renaming,
	oldStackCfKey,
}: {
	stackCfKey: string;
	renaming?: boolean;
	oldStackCfKey?: string;
}) {
	useRecoilValue(LocaleAtom); // re-render on locale change
	const [stackCfKeyAtom, setStackCfKeyAtom] = useRecoilState(StackCfKeyAtom);
	const {
		register,
		handleSubmit,
		setFocus,
		formState: { errors },
		reset,
	} = useForm<FormValue>();
	const auth = useRecoilValue(Authorization);
	const hostId = useRecoilValue(HostId);
	const isInLandscape = useRecoilValue(Orientation) === "landscape";
	const level = useRecoilValue(CurrentlySelectedFilePath);
	const ownerId = useRecoilValue(OwnerId);
	const ownerName = useRecoilValue(OwnerName);
	const refreshStackFileNames =
		useRecoilRefresher_UNSTABLE(StackCfKeysSelector);
	const setCreateNewStack = useSetRecoilState(CreateNewStack);
	const setIsUploading = useSetRecoilState(IsUploading);
	const setRename = useSetRecoilState(RenameFamily(stackCfKey));
	const setShowStacks = useSetRecoilState(ShowStacks);
	const setShowTknsPanel = useSetRecoilState(ShowTknsPanelAtom);
	const stackCfKeys = useRecoilValue(StackCfKeysSelector);
	const setOpenedInTabStories = useSetRecoilState(OpenedInTabStories);

	const close = () => {
		if (renaming) {
			setRename(false);
		} else {
			setCreateNewStack(false);
		}
	};

	const handleStackName = async ({ userDefinedStackName }: FormValue) => {
		if (!ownerName) return;
		const user = getAuth().currentUser;
		if (!user) return;
		const jwt = await getIdToken(user);

		userDefinedStackName = userDefinedStackName
			.trim()
			.replace(/\s/g, "-")
			.toLowerCase(); // the userDefinedStackName is the name without the ownerName
		const stackCfKey = createKey({
			stackFileName: userDefinedStackName,
			ownerName,
		}); // the stackFileName is the full path that will be used as the key in the KV store
		if (stackCfKeys.find((val) => stackCfKey === val?.key)) {
			notify(text.stacks.storyExists);
			return;
		}

		if (!auth.isLoggedIn || !ownerId || !ownerName) {
			notify(text.stacks.pleaseLogin);
			return;
		}

		setIsUploading(true);

		// TODO: Messages.tsx doesn't get the state update for stackFileName before saving.
		const client = new GraphQLClient(clientConfig.gqlWriteStack());
		const sdk = getSdk(client);
		// the backend rejects empty/placeholder code, and without a persisted
		// record the stack fetch fails, saves get keyed to "unset", and the new
		// story is silently lost — so create with real starter code
		// `language:` is auto-filled from the author's browser (WCAG 3.1.1) so
		// every deck declares a language without the author having to think about
		// it; they can edit it if the content is in another language.
		const starterCode = `language: ${detectBrowserLanguage()}\ntitle: ${userDefinedStackName.replace(/-/g, " ")}`;
		const stack: SDK_UpdateStackMutationVariables = {
			audioFiles: [],
			code: starterCode,
			created: 0,
			dataFiles: [],
			filters: [],
			images: [],
			jwt,
			navigationType: moveByOptions[defaultNavigationIndex],
			ownerName: ownerName,
			publish: false,
			stackCfKey,
			stackId: nanoid(26),
			updated: 0,
		};

		// choose which API to use based on "renaming" value
		try {
			if (renaming && oldStackCfKey) {
				await sdk.RenameStack({
					jwt,
					ownerName,
					stackCfKey: oldStackCfKey,
					newStackCfKey: stackCfKey,
				});
				setRename(false);

				setOpenedInTabStories((prev) =>
					prev.map((story) => (story === oldStackCfKey ? stackCfKey : story)),
				);

				if (stackCfKeyAtom === oldStackCfKey) {
					setStackCfKeyAtom(stackCfKey);
				}
				// setShowStacks(false);
			} else {
				console.log("Creating new stack from EditableFile.tsx", stack);
				const result = await sdk.UpdateStack(stack);
				if (!result.updateStack?.success) {
					// Turn the resolver's numeric return code into a friendly
					// message; a free-tier wall also opens the Plus panel.
					const code = result.updateStack?.message;
					setIsUploading(false);
					setShowStacks(false);
					notify(returnCodeMessage(code));
					if (isUpgradeCode(code)) setShowTknsPanel(true);
					return;
				}
				setCreateNewStack(false);
				setOpenedInTabStories((prev) => [...prev, stackCfKey]);
				setStackCfKeyAtom(stackCfKey);
				setShowStacks(false);
				// force the viewer to clear.
				sendCodeToViewer(hostId, emptyStack);
			}

			setIsUploading(false);
			refreshStackFileNames();

			reset(); // resetForm
		} catch (error) {
			notify(text.stacks.createError);
			setIsUploading(false);
			setShowStacks(false);
			console.error(error);
		}
	};

	const placeholderText = renaming
		? `${oldStackCfKey ? oldStackCfKey.replace(`${ownerName}/`, "") : ""}`
		: text.stacks.storyName;

	const stackPath = level ? `${level.slice(1).join("/")}/` : "";

	useEffect(() => {
		// animate scrolling the panel to the top and focus input
		const panel = document.getElementById("stories");
		const panelIsScrolled = panel?.scrollTop === 0;

		if (panelIsScrolled) {
			setFocus("userDefinedStackName");
		}

		const setFocusToInput = () => {
			if (panel && panel.scrollTop === 0) {
				setFocus("userDefinedStackName");
			}
		};

		if (panel && !panelIsScrolled) {
			panel.addEventListener("scroll", setFocusToInput);
			panel.scrollTo({ top: 0, behavior: "smooth" });
		}

		return () => panel?.removeEventListener("scroll", setFocusToInput);
	}, [setFocus]);

	const triggerSubmit = () => {
		handleSubmit(handleStackName)();
	};

	return (
		<StyledFile $isInLandscape={isInLandscape} $noRollOver={!renaming}>
			<div />
			<div />

			<form className="form-row" onSubmit={handleSubmit(handleStackName)}>
				<div className="form-input-stack-name">
					{isInLandscape && <div className="form-owner-name">{ownerName}/</div>}
					<input
						data-testid="userDefinedStackName"
						aria-invalid={!!errors.userDefinedStackName}
						placeholder={placeholderText}
						{...register("userDefinedStackName", {
							required: true,
							maxLength: 77,
							minLength: 3,
							value: renaming
								? placeholderText
								: stackPath.length === 1
									? ""
									: stackPath, // sorry :(
							validate: (rawValue) => {
								const value = rawValue.trim();

								if (!value) {
									return text.stacks.enterName;
								}

								if (value.length < 3) {
									return text.stacks.nameTooShort;
								}

								if (value.length > 77) {
									return text.stacks.nameTooLong;
								}

								if (!urlSafeRegEx.test(value)) {
									return text.stacks.invalidChars;
								}

								if (/\s/.test(value)) {
									return text.stacks.noSpaces;
								}

								if (reservedWordsRegEx.exec(value)) {
									return text.stacks.reservedWords;
								}

								if (trailingSlashRegEX.test(value)) {
									return text.stacks.needNameAfterFolder;
								}

								return true;
							},
						})}
					/>
				</div>

				<div className="form-input-submit">
					<StyledPillButton
						$variant="dark"
						onClick={triggerSubmit}
						className="create-button"
						data-testid={renaming ? "rename-ok-button" : "create-button"}
					>
						{renaming ? text.ui.ok : text.ui.create}
					</StyledPillButton>
					<StyledPillButton $variant="danger" onClick={() => close()}>
						{text.ui.cancel}
					</StyledPillButton>
				</div>
			</form>

			{errors.userDefinedStackName &&
				errors.userDefinedStackName.type === "minLength" && (
					<StyledError role="alert">
						{text.stacks.nameTooShortShort}
					</StyledError>
				)}
			{errors.userDefinedStackName &&
				errors.userDefinedStackName.type === "validate" && (
					<StyledError role="alert">
						{errors.userDefinedStackName.message}
					</StyledError>
				)}
			{errors.userDefinedStackName &&
				errors.userDefinedStackName.type === "pattern" && (
					<StyledError role="alert">
						{text.stacks.tryRemovingSpecialChars}
					</StyledError>
				)}
			{errors.userDefinedStackName &&
				errors.userDefinedStackName.type === "maxLength" && (
					<StyledError role="alert">{text.stacks.nameTooLongShort}</StyledError>
				)}
			{/*{errors.stackName && <StyledError role="alert">A name is required</StyledError>}*/}
		</StyledFile>
	);
}
